import { createElement, init } from "@airwallex/components-sdk";
import React, { HTMLAttributes, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { initAirwallexIntent } from "../../../apis/subscription";
import { customShowToast } from "../../../helper/toast";
import type { TFPSSuccessResponse } from "./type";

interface IAirWallexFPS {
  paymentId?: string;
  returnUrl?: string;
}

type TAirWallexFPSProps = IAirWallexFPS & HTMLAttributes<HTMLDivElement>;

const AirwallexFPSBlock: React.FC<TAirWallexFPSProps> = (props) => {
  const { paymentId, returnUrl, ...otherProps } = props;
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!paymentId) return;

    let element: any = null;
    let domElement: HTMLElement | null = null;

    const loadDropInElement = async () => {
      setIsLoading(true);
      try {
        await init({
          env: process.env.REACT_APP_ENV === "production" ? "prod" : "demo",
          enabledElements: ["payments"],
        });

        const intent = await initAirwallexIntent(
          +paymentId,
          returnUrl ?? `${window.location.href}?fps=hk`
        );
        const { intentId, clientSecret, currency } = intent;

        element = await createElement(`dropIn`, {
          intent_id: intentId,
          client_secret: clientSecret,
          currency,
          methods: ["fps"],
          appearance: {
            mode: "light",
            variables: {
              colorBrand: "#612FFF",
            },
          },
        });

        element?.mount(`fps-dropIn${paymentId}`);

        // Attach event listeners after mount
        domElement = document.getElementById(`fps-dropIn${paymentId}`);
        if (domElement) {
          domElement.addEventListener("onReady", onReady as EventListener);
          domElement.addEventListener("onSuccess", onSuccess as EventListener);
          domElement.addEventListener("onError", onError as EventListener);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    // Event handlers
    const onReady = (event: CustomEvent) => {
      // console.log(`Element is mounted: ${JSON.stringify(event.detail)}`);
    };
    const onSuccess = (event: CustomEvent<TFPSSuccessResponse>) => {
      const url = event.detail.intent.return_url;

      if (url.startsWith("http")) {
        window.location.href = url;
      } else {
        navigate(url);
      }
    };
    const onError = (event: CustomEvent) => {
      const { error } = event.detail;
      console.error("There is an error", error);
      customShowToast("付款失敗", "error");
    };

    loadDropInElement();

    // Cleanup
    return () => {
      if (domElement) {
        domElement.removeEventListener("onReady", onReady as EventListener);
        domElement.removeEventListener("onSuccess", onSuccess as EventListener);
        domElement.removeEventListener("onError", onError as EventListener);
      }
      // Optionally, unmount the element if SDK supports it
      // element?.unmount?.();
    };
  }, [navigate, paymentId, returnUrl]);

  return (
    <div className="text-white" {...otherProps}>
      <div id={`fps-dropIn${paymentId}`} />
      {isLoading && <div>Loading FPS Payment...</div>}
    </div>
  );
};

export default AirwallexFPSBlock;
